import React, { useMemo } from 'react';
import {
  AppointmentStatusHistoryEnum,
  AppointmentStatusHistoryEntry,
} from 'graphql/generated/hasura';
import StatusHistoryRow from 'components/StatusHistoryRow';
import { formatDateForAppointment } from 'utilities/functions';
import { APPOINTMENT_STATUS_HISTORY_LABELS } from '../../../../const/appointments-labels';

const fullHistory = [
  AppointmentStatusHistoryEnum.BookedNotPaid,
  AppointmentStatusHistoryEnum.Upcoming,
  AppointmentStatusHistoryEnum.Completed,
  AppointmentStatusHistoryEnum.CompletedSummaryReady,
];

const statusClassName = {
  [AppointmentStatusHistoryEnum.Canceled]:
    '!text-clc-red border capitalize border-clc-red bg-red-50',
  [AppointmentStatusHistoryEnum.PatientMissed]:
    '!text-clc-red border capitalize border-clc-red bg-red-50',
  [AppointmentStatusHistoryEnum.ProviderMissed]:
    '!text-clc-red border capitalize border-clc-red bg-red-50',
  [AppointmentStatusHistoryEnum.NoShow]:
    '!text-clc-red border capitalize border-clc-red bg-red-50',
  [AppointmentStatusHistoryEnum.Completed]:
    '!text-bright-green border capitalize border-bright-green bg-green-50',
  [AppointmentStatusHistoryEnum.CompletedSummaryReady]:
    '!text-bright-green border capitalize border-bright-green bg-green-50',
  [AppointmentStatusHistoryEnum.Upcoming]:
    '!text-clc-blue capitalize border border-clc-blue bg-positive',
  [AppointmentStatusHistoryEnum.BookedNotPaid]:
    '!text-amber-border border capitalize border-amber-border bg-amber-50',
} as Record<AppointmentStatusHistoryEnum, string>;

interface StatusHistoryProps {
  statusHistory: Array<AppointmentStatusHistoryEntry>;
}

const StatusHistory: React.FC<StatusHistoryProps> = ({ statusHistory }) => {
  const displayItems = useMemo(() => {
    const items: Array<{
      status: AppointmentStatusHistoryEnum;
      className?: string;
      datetime: string | null;
      isInterrupting: boolean;
    }> = [];
    let isAborted = false;

    const statusHistoryMap = new Map(
      statusHistory.map((entry) => [entry.status, entry]),
    );

    if (statusHistoryMap.get(fullHistory[fullHistory.length - 1])) {
      fullHistory.forEach((status) =>
        items.push({
          status: status,
          className: statusClassName[status],
          datetime: statusHistoryMap.get(status)
            ? formatDateForAppointment(
                statusHistoryMap.get(status)?.datetime as string,
                {
                  simplified: true,
                  withTimeZone: false,
                },
              )
            : '',
          isInterrupting: false,
        }),
      );

      return items;
    }

    for (const historyEntry of statusHistory) {
      const isStandardStatus = fullHistory.includes(historyEntry.status);
      const isCompletedStatus =
        historyEntry.status ===
        AppointmentStatusHistoryEnum.CompletedSummaryReady;

      items.push({
        status: historyEntry.status,
        className: statusClassName[historyEntry.status],
        datetime: formatDateForAppointment(historyEntry.datetime, {
          simplified: true,
          withTimeZone: false,
        }),
        isInterrupting: !isStandardStatus,
      });

      if (!isStandardStatus || isCompletedStatus) {
        isAborted = true;
        break;
      }
    }

    if (!isAborted) {
      const completedStatuses = items.map((item) => item.status);

      for (const standardStatus of fullHistory) {
        if (!completedStatuses.includes(standardStatus)) {
          items.push({
            status: standardStatus,
            datetime: null,
            isInterrupting: false,
          });
        }
      }
    }

    return items;
  }, [statusHistory]);

  return (
    <>
      {displayItems.map((status, index) => {
        return (
          <StatusHistoryRow
            key={`${index}-${status.status}`}
            index={index}
            label={
              APPOINTMENT_STATUS_HISTORY_LABELS[status.status] ?? status.status
            }
            time={status.datetime}
            labelClassName={status.className}
            standardLabelClassName="capitalize"
            hasNoInStandard={status.isInterrupting}
            isLast={index === displayItems.length - 1}
          />
        );
      })}
    </>
  );
};

export default StatusHistory;
